import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle, TrendingDown, Check, X } from 'lucide-react';
import { CompanySubscriptionTier } from '@/types/billing';
import { useTranslation } from '@/hooks/useTranslation';
import { InlineLoader } from '@/components/ui/loading';

interface SubscriptionDowngradeDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  currentTier: CompanySubscriptionTier | null;
  newTier: CompanySubscriptionTier;
  isLoading: boolean;
}

export const SubscriptionDowngradeDialog: React.FC<SubscriptionDowngradeDialogProps> = ({
  open,
  onClose,
  onConfirm,
  currentTier,
  newTier,
  isLoading,
}) => {
  const { t } = useTranslation('workspace');

  const isCurrentlyPaid = currentTier && currentTier.tier_key !== 'free';

  return (
    <AlertDialog open={open} onOpenChange={onClose}>
      <AlertDialogContent className="sm:max-w-lg">
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-full bg-orange-100 dark:bg-orange-900/20">
              <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-500" />
            </div>
            <AlertDialogTitle className="text-xl">
              {isCurrentlyPaid 
                ? t('cancel_subscription_title') 
                : t('downgrade_to_free_title')}
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription className="text-base">
            <div className="space-y-4 pt-2">
              <p>
                {t('downgrade_description', {
                  currentPlan: currentTier?.tier_name || 'Current Plan',
                  newPlan: newTier.tier_name,
                })}
              </p>

              {/* What you'll lose */}
              <div className="space-y-2">
                <h4 className="font-semibold text-foreground flex items-center gap-2">
                  <TrendingDown className="h-4 w-4 text-orange-600 dark:text-orange-500" />
                  {t('what_you_will_lose')}
                </h4>
                <ul className="space-y-2 text-sm">
                  {currentTier && (
                    <>
                      <li className="flex items-start gap-2">
                        <X className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
                        <span>
                          {t('lose_monthly_credits', {
                            oldCredits: currentTier.monthly_credits,
                            newCredits: newTier.monthly_credits,
                          })}
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <X className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
                        <span>
                          {t('lose_job_posting_limit', {
                            oldLimit: currentTier.job_posting_limit || '∞',
                            newLimit: newTier.job_posting_limit || '1',
                          })}
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <X className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
                        <span>{t('lose_premium_features')}</span>
                      </li>
                    </>
                  )}
                </ul>
              </div>

              {/* What you'll keep */}
              <div className="space-y-2">
                <h4 className="font-semibold text-foreground flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-600 dark:text-green-500" />
                  {t('what_you_will_keep')}
                </h4>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-600 dark:text-green-500 flex-shrink-0 mt-0.5" />
                    <span>{t('keep_platform_access')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-600 dark:text-green-500 flex-shrink-0 mt-0.5" />
                    <span>{t('keep_existing_jobs')}</span>
                  </li>
                </ul>
              </div>

              {/* Important note */}
              <div className="bg-muted rounded-lg p-3">
                <p className="text-sm text-muted-foreground">
                  {t('subscription_cancel_note')}
                </p>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          <AlertDialogCancel disabled={isLoading} onClick={onClose}>
            {t('keep_current_plan')}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={isLoading}
            className="bg-destructive hover:bg-destructive/90"
          >
            {isLoading ? (
              <>
                <InlineLoader size="sm" text="" />
                {t('processing_cancellation')}
              </>
            ) : (
              t('confirm_downgrade')
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
